import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import API_BASE_URL from "../config";

const Sidebar = () => {
  const token = localStorage.getItem("token");
  let userType = null;
  try {
    if (token) {
      userType = jwtDecode(token).type; // 'user', 'customer', or 'both'
    }
  } catch (err) {
    console.error("Token decode error:", err);
  }

  const location = useLocation();
  const activeRole = localStorage.getItem("activeRole") || userType;

  const [companiesCount, setCompaniesCount] = useState(0);
  const [branchesCount, setBranchesCount] = useState(0);
  const [selectedCompany] = useState(() => {
    const stored = localStorage.getItem("selectedCompany");
    return stored ? JSON.parse(stored) : null;
  });

  // Global sidebar collapse/expand (persisted in localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;

    if (newState) {
      // When collapsing, keep only dropdowns that have an active child
      // so the submenu stays visible with proper underline indicator
      const items = getNavItems();
      const toKeepOpen = {};
      items.forEach((item, i) => {
        if (item.dropdown && isActive(item.dropdown)) {
          toKeepOpen[`main-${i}`] = true;
        }
      });
      setOpenDropdowns(toKeepOpen);
    }

    setSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", newState);
  };

  const [openDropdowns, setOpenDropdowns] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetch companies count
  useEffect(() => {
    if ((activeRole === "user" || userType === "both") && token) {
      axios
        .get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setCompaniesCount(res.data?.length || 0))
        .catch(() => setCompaniesCount(0));
    }
  }, [activeRole, userType, token]);

  // Fetch branches count
  useEffect(() => {
    if (selectedCompany && activeRole === "user") {
      axios
        .get(`${API_BASE_URL}/companies/${selectedCompany.id}/branches`)
        .then((res) => setBranchesCount(res.data?.length || 0))
        .catch(() => setBranchesCount(0));
    }
  }, [selectedCompany, activeRole]);

  // Keep dropdowns open when route is active (only meaningful when expanded)
  useEffect(() => {
    if (sidebarCollapsed) return;

    const items = getNavItems();
    const open = {};
    items.forEach((item, i) => {
      if (item.dropdown && isActive(item.dropdown)) open[`main-${i}`] = true;
      item.dropdown?.forEach((sub, j) => {
        if (sub.subDropdown && isActive(sub.subDropdown))
          open[`sub-${j}`] = true;
      });
    });
    setOpenDropdowns(open);
  }, [location.pathname, sidebarCollapsed]);

  const getNavItems = () => {
    const items = [];

    // Customer role
    if (activeRole === "customer") {
      items.push({
        path: "/manage-bookings",
        label: "Appointments",
        icon: "bi-calendar",
      });
      items.push({ path: "/profile", label: "Profile", icon: "bi-person" });
    }

    // User / Manager role
    if (activeRole === "user" || activeRole === "manager") {
      // 1. Events — top level
      items.push({
        label: "Events",
        icon: "bi-calendar-event",
        dropdown: [
          { path: "/events", label: "Event Types", icon: "bi-calendar-event" },
          { path: "/bookings", label: "Bookings", icon: "bi-journal-check" },
          { path: "/availability", label: "Availability", icon: "bi-clock" },
          {
            path: "/meeting-integration",
            label: "Meeting Integration",
            icon: "bi-camera-video",
          },
        ],
      });

      // 2. Companies — top level
      items.push({
        label: "Companies",
        icon: "bi-building",
        dropdown: [
          {
            path: "/companies",
            label: "Manage Companies",
            icon: "bi-building",
          },
          {
            path: "/manage-company-bookings",
            label: "Manage Bookings",
            icon: "bi-calendar-check",
          },
          { path: "/manage-customers", label: "Customers", icon: "bi-people" },
          {
            path: "/manage-billing-taxes",
            label: "Manage Billing & Taxes",
            icon: "bi-receipt",
          },
          { path: "/reports", label: "Reports", icon: "bi-bar-chart" },
        ],
      });

      // 3. Settings — NOW A SEPARATE TOP-LEVEL MENU (NOT nested!)
      items.push({
        label: "Settings",
        icon: "bi-gear",
        dropdown: [
          {
            path: "/settings/local-tax",
            label: "Manage Local Tax",
            icon: "bi-percent",
          },
          {
            path: "/settings/discounts",
            label: "Discounts & Offers",
            icon: "bi-tag",
          },
          {
            path: "/settings/packages",
            label: "Manage Packages",
            icon: "bi-box",
          },
          {
            path: "/settings/business-hours",
            label: "Business Hours & Closures",
            icon: "bi-clock-history",
          },
        ],
      });

      // Switch Company / Branch
      if (companiesCount > 1) {
        items.push({
          path: "/select-company",
          label: "Switch Company",
          icon: "bi-arrow-repeat",
        });
      }
      if (branchesCount > 1) {
        items.push({
          path: "/select-branch",
          label: "Switch Branch",
          icon: "bi-arrow-repeat",
        });
      }
    }

    // Switch Role for "both"
    if (userType === "both") {
      items.push({
        path: "/role-select",
        label: "Switch Role",
        icon: "bi-person-gear",
      });
    }

    return items;
  };

  const isActive = (arr) =>
    arr?.some((i) =>
      i.subDropdown
        ? i.subDropdown.some((x) => x.path === location.pathname)
        : i.path === location.pathname,
    );

  const navItems = getNavItems();

  // Dynamic sidebar width for smooth collapse
  const sidebarWidth = sidebarCollapsed ? "90px" : "280px";

  return (
    <>
      {/* PERFECT HAMBURGER → X ANIMATION (Mobile only) */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="hamburger-btn d-md-none"
        style={{
          position: "fixed",
          top: "14px",
          left: "6px",
          width: "48px",
          height: "48px",
          background: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(83,178,21,0.3)",
          borderRadius: "50%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "24px",
            height: "20px",
            right: "10px",
          }}
        >
          <span
            style={{
              position: "absolute",
              width: "100%",
              height: "3px",
              background: "#53b215",
              borderRadius: "3px",
              transition: "all 0.3s ease",
              top: mobileOpen ? "8.5px" : "0",
              transform: mobileOpen ? "rotate(45deg)" : "rotate(0)",
            }}
          ></span>
          <span
            style={{
              position: "absolute",
              width: "100%",
              height: "3px",
              background: "#53b215",
              borderRadius: "3px",
              transition: "all 0.3s ease",
              top: "8.5px",
              opacity: mobileOpen ? 0 : 1,
            }}
          ></span>
          <span
            style={{
              position: "absolute",
              width: "100%",
              height: "3px",
              background: "#53b215",
              borderRadius: "3px",
              transition: "all 0.3s ease",
              top: mobileOpen ? "8.5px" : "17px",
              transform: mobileOpen ? "rotate(-45deg)" : "rotate(0)",
            }}
          ></span>
        </div>
      </button>

      {/* Overlay (Mobile) */}
      {mobileOpen && (
        <div
          className="sidebar-overlay open"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`main-sidebar ${mobileOpen ? "open" : ""} ${
          sidebarCollapsed ? "sidebar-collapsed" : ""
        }`}
        style={{
          width: sidebarWidth,
          transition: "width 0.25s ease",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Subtle Gradient Vertical Separator Line (right edge) */}
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "16px",
            bottom: "16px",
            width: "2.5px",
            background:
              "linear-gradient(to bottom, #53b215 0%, #3a9c0f 35%, #53b215 100%)",
            opacity: 0.22,
            borderRadius: "3px",
            zIndex: 2,
          }}
        />

        <div className="p-4 pt-5">
          {/* Desktop Sidebar Collapse/Expand Toggle Button */}
          <div className="d-none d-md-flex align-items-center justify-content-end mb-3 px-1">
            <button
              onClick={toggleSidebarCollapse}
              className="btn btn-sm p-1 text-dark border-0"
              style={{
                background: "rgba(0,0,0,0.04)",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <i
                className={`bi ${
                  sidebarCollapsed
                    ? "bi-chevron-double-right"
                    : "bi-chevron-double-left"
                } fs-5`}
              />
            </button>
          </div>

          {navItems.map((item, i) =>
            item.dropdown ? (
              <div key={i} className="mb-1">
                {/* Parent Button */}
                <button
                  onClick={() =>
                    setOpenDropdowns((p) => ({
                      ...p,
                      [`main-${i}`]: !p[`main-${i}`],
                    }))
                  }
                  className={`w-100 d-flex align-items-center px-3 py-3 rounded-3 text-decoration-none border-0 text-start mb-1 position-relative
                    ${
                      isActive(item.dropdown)
                        ? "text-success fw-bold"
                        : "text-dark hover-bg-light"
                    }`}
                  style={{ background: "transparent", minHeight: "48px" }}
                >
                  {/* Icon + Label (label hidden when collapsed) */}
                  <span className="d-flex align-items-center flex-grow-1">
                    <i className={`bi ${item.icon} fs-5`} />
                    {!sidebarCollapsed && (
                      <span className="ms-3 text-nowrap">{item.label}</span>
                    )}
                  </span>

                  {/* Chevron - ALWAYS fixed on the right side */}
                  {!sidebarCollapsed && (
                    <i
                      className={`bi ${
                        openDropdowns[`main-${i}`]
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } fs-6 text-muted`}
                      style={{ marginLeft: "auto" }}
                    />
                  )}
                </button>

                {/* Submenu - Always rendered so icons are visible in collapsed mode */}
                <Collapse in={!!openDropdowns[`main-${i}`]}>
                  <div>
                    {/* Visual hierarchy in collapsed mode: indent + left connector line */}
                    <div className={sidebarCollapsed ? "ps-3 ms-3 border-start border-success border-opacity-25" : "ps-4"}>
                      {item.dropdown.map((sub, j) => {
                        const subActive = location.pathname === sub.path;

                        // Professional styling: underline instead of background fill when collapsed
                        const collapsedActiveStyle = sidebarCollapsed && subActive
                          ? {
                              borderBottom: "2.5px solid #53b215",
                              borderRadius: "2px",
                              paddingBottom: "4px",
                            }
                          : {};

                        return (
                          <Link
                            key={j}
                            to={sub.path}
                            onClick={() => setMobileOpen(false)}
                            className={`d-flex align-items-center rounded-3 text-decoration-none mb-1
                              ${
                                subActive
                                  ? "text-success fw-bold"
                                  : "text-dark hover-bg-light"
                              }
                              ${
                                sidebarCollapsed
                                  ? "justify-content-center py-2 px-2"
                                  : "px-3 py-2"
                              }`}
                            style={{
                              fontSize: sidebarCollapsed ? "1.05rem" : "0.95rem",
                              minHeight: sidebarCollapsed ? "38px" : "auto",
                              ...collapsedActiveStyle,
                            }}
                          >
                            <i
                              className={`bi ${sub.icon} ${
                                sidebarCollapsed ? "" : "me-3"
                              }`}
                            />
                            {!sidebarCollapsed && sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </Collapse>
              </div>
            ) : (
              // Regular single menu link (no dropdown)
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`d-flex align-items-center px-3 py-3 rounded-3 text-decoration-none mb-2
                  ${
                    location.pathname === item.path
                      ? "bg-success text-white fw-bold shadow-sm"
                      : "text-dark hover-bg-light"
                  }`}
                style={{ minHeight: "48px" }}
              >
                <i className={`bi ${item.icon} fs-5`} />
                {!sidebarCollapsed && (
                  <span className="ms-3 text-nowrap">{item.label}</span>
                )}
              </Link>
            ),
          )}
        </div>
      </aside>

      {/* Desktop offset spacer - matches current sidebar width */}
      <div
        className="d-none d-md-block"
        style={{
          minWidth: sidebarWidth,
          transition: "min-width 0.25s ease",
        }}
      />
    </>
  );
};

export default Sidebar;
